import { Routes } from '@angular/router';
import { APP_ROUTES } from './app.routes.model';
import { MainLayoutComponent } from '@shared/layouts/main-layout/main-layout.component';
import { hdAuthGuard } from '@core/guards/hdAuth.guard';
import { isAuthenticatedGuard } from '@core/guards/isAuthenticated.guard';
import { orgAuthGuard } from '@core/guards/orgAuth.guard';
import { isNotAuthenticatedGuard } from '@core/guards/isNotAuthenticated.guard';
import { hasFeatureEnabledGuard } from '@core/guards/hasFeatureEnabled.guard';
import {
  ActionGuardedData,
  FeatureGuardedData,
  IsTeamMemberGuardData,
} from '@shared/utils/route-data-types';
import { isTeamMemberGuard } from '@core/guards/isTeamMember.guard';
import { hasAccessForActionGuard } from '@core/guards/hasAccessForAction.guard';
import { RBAC_ACTIONS } from '@api/model/rbac-actions';

/**
 * Routes accessible only for HD
 */
const HdRoutes: Routes = [
  {
    path: APP_ROUTES.dashboard,
    loadComponent: () =>
      import('./features/dashboard/dashboard.component').then(
        (c) => c.DashboardComponent,
      ),
  },
  {
    path: APP_ROUTES.organizations.root,
    canActivateChild: [hasAccessForActionGuard],
    data: {
      action: RBAC_ACTIONS.organizationList,
    } as ActionGuardedData,
    loadChildren: () =>
      import(
        '@features/organization-mgmt/organizations/organizations-hd.routes'
      ).then((r) => r.OrganizationsHdRoutes),
  },
  {
    path: APP_ROUTES.platformAdministration.root,
    canActivateChild: [hasFeatureEnabledGuard],
    data: {
      feature: 'platformAdministration',
    } as FeatureGuardedData,
    loadChildren: () =>
      import(
        '@features/platform-administration/platform-administration.routes'
      ).then((r) => r.PlatformAdministrationRoutes),
  },
  {
    path: APP_ROUTES.platformAdministration.hdUserManagement,
    canActivateChild: [hasFeatureEnabledGuard],
    data: {
      feature: 'platformAdministration',
    } as FeatureGuardedData,
    loadChildren: () =>
      import('@features/user-administration/user-administration.routes').then(
        (r) => r.UserAdministrationRoutes,
      ),
  },
  {
    path: APP_ROUTES.platformAdministration.orgUserManagement,
    canActivateChild: [hasFeatureEnabledGuard],
    data: {
      feature: 'platformAdministration',
    } as FeatureGuardedData,
    loadChildren: () =>
      import(
        '@features/org-user-administration/user-administration.routes'
      ).then((r) => r.OrgUserAdministrationRoutes),
  },
  {
    path: '',
    redirectTo: APP_ROUTES.dashboard,
    pathMatch: 'full',
  },
];

/**
 * Application tabs
 *
 */
export enum DEAL_TABS {
  DESCRIPTION = 'description',
  CONTRACTS = 'contracts',
  DELIVERABLES_AND_SAMPLES = 'deliverables-samples',
  PAYMENTS_AND_QUOTES = 'payments-quotes',
  REQUEST_MESSAGING = 'requestMessaging',
  OFFERING_MESSAGING = 'offeringMessaging',
}

export const AppRoutes: Routes = [
  {
    path: APP_ROUTES.signIn,
    canActivate: [isNotAuthenticatedGuard],
    loadChildren: () =>
      import('./features/auth/sign-in/sign-in.routes').then(
        (r) => r.SignInRoutes,
      ),
  },
  {
    path: APP_ROUTES.resetPassword.root,
    loadChildren: () =>
      import('./features/auth/reset-password/reset-password.routes').then(
        (r) => r.ResetPasswordRoutes,
      ),
  },
  {
    path: APP_ROUTES.signUp.root,
    loadChildren: () =>
      import('./features/auth/sign-up/sign-up.routes').then(
        (r) => r.SignUpRoutes,
      ),
  },
  {
    path: APP_ROUTES.invitation,
    loadChildren: () =>
      import('./features/auth/invitation/invitation.routes').then(
        (r) => r.InvitationRoutes,
      ),
  },
  {
    path: APP_ROUTES.bidInviteDecline,
    loadComponent: () =>
      import('./shared/layouts/auth-layout/auth-layout.component').then(
        (c) => c.AuthLayoutComponent,
      ),
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            './features/other-public/bid-invite-declined/bid-invite-declined.component'
          ).then((c) => c.BidInviteDeclinedComponent),
      },
    ],
  },
  // TODO remove/comment out once going to production
  {
    path: 'dev/components-preview',
    loadComponent: () =>
      import('./dev/components-preview/components-preview.component').then(
        (c) => c.ComponentsPreviewComponent,
      ),
  },
  {
    path: APP_ROUTES.home,
    component: MainLayoutComponent,
    canMatch: [isAuthenticatedGuard],
    children: [
      {
        path: APP_ROUTES.profile,
        loadComponent: () =>
          import(
            '@features/organization-mgmt/user-profile/user-profile.component'
          ).then((c) => c.UserProfileComponent),
      },
      {
        path: APP_ROUTES.organizations.myOrganization,
        canActivateChild: [orgAuthGuard],
        loadChildren: () =>
          import(
            '@features/organization-mgmt/organizations/organizations.routes'
          ).then((r) => r.OrganizationsRoutes),
      },
      {
        path: APP_ROUTES.requests.root,
        canActivate: [isTeamMemberGuard],
        data: {
          pageName: 'requests',
        } as IsTeamMemberGuardData,
        loadChildren: () =>
          import('@features/data-listing/requests/requests.routes').then(
            (r) => r.RequestsRoutes,
          ),
      },
      {
        path: APP_ROUTES.offerings.root,
        canActivate: [isTeamMemberGuard],
        data: {
          pageName: 'offerings',
        } as IsTeamMemberGuardData,
        loadChildren: () =>
          import('@features/data-listing/offerings/offerings.routes').then(
            (r) => r.OfferingsRoutes,
          ),
      },
      {
        path: APP_ROUTES.deals.root,
        canActivate: [isTeamMemberGuard],
        data: {
          pageName: 'deals',
        } as IsTeamMemberGuardData,
        loadChildren: () =>
          import('@features/deals/deals.routes').then((r) => r.DealsRoutes),
      },
      {
        path: APP_ROUTES.matches.root,
        canActivate: [isTeamMemberGuard],
        data: {
          pageName: 'matches',
        } as IsTeamMemberGuardData,
        loadChildren: () =>
          import('@features/matches/matches.routes').then(
            (r) => r.MatchesRoutes,
          ),
      },
      {
        path: APP_ROUTES.hdZone,
        canActivateChild: [hdAuthGuard],
        children: HdRoutes,
      },
      {
        path: APP_ROUTES.notTeamMember,
        loadComponent: () =>
          import(
            'app/features/error-pages/not-team-member/not-team-member.component'
          ).then((c) => c.NotTeamMemberComponent),
      },
      {
        path: APP_ROUTES.notAllowed,
        loadComponent: () =>
          import(
            'app/features/error-pages/not-allowed/not-allowed.component'
          ).then((c) => c.NotAllowedComponent),
      },
      {
        path: APP_ROUTES.generalErrorPage,
        loadComponent: () =>
          import(
            'app/features/error-pages/general-error/general-error.component'
          ).then((c) => c.GeneralErrorComponent),
      },
      {
        path: '',
        redirectTo: APP_ROUTES.organizations.myOrganization,
        pathMatch: 'full',
      },
      {
        path: '**',
        loadComponent: () =>
          import('@features/error-pages/not-found/not-found.component').then(
            (c) => c.NotFoundComponent,
          ),
      },
    ],
  },
  {
    path: '**',
    redirectTo: APP_ROUTES.signIn,
    pathMatch: 'full',
  },
];
